import type { ReactNode } from "react";
import { useRef } from "react";
import type { TypedWrappedFieldProps } from "redux-form";
import { Field } from "redux-form";

import type { StackProps } from "@braintrust/braintrust-ui-components";
import {
  Box,
  Checkbox,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { PdfViewer } from "@js/apps/common/components";
import { ModalInstance } from "@js/components/modal";
import { requiredWithSpecificMessage } from "@js/forms/validators";
import { useResizeObserver } from "@js/hooks/use-resize-observer";
import type {
  ContractTerms,
  EmployerOffer,
  FreelancerOffer,
} from "@js/types/jobs";

import { offerRequiresAcceptance } from "../../utils";

import styles from "./styles.module.scss";

type BoilerplateTermsSectionWrapperProps = {
  children: ReactNode;
} & Pick<StackProps, "sx">;

const BoilerplateTermsSectionWrapper = ({
  children,
  sx,
}: BoilerplateTermsSectionWrapperProps) => {
  return (
    <Stack
      sx={{
        backgroundColor: "var(--white)",
        borderRadius: 4,
        border: "1px solid var(--soft-beige)",
        py: 4,
        "> *": {
          px: 4,
        },
        ...sx,
      }}
    >
      {children}
    </Stack>
  );
};

const defaultPpfWidth = 735;

type ContractTermsPreviewProps = {
  contractTerms: ContractTerms;
};
const ContractTermsPreview = ({ contractTerms }: ContractTermsPreviewProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const dimensions = useResizeObserver(ref);
  const pdfWidth = dimensions?.width ?? defaultPpfWidth;
  return (
    <Box
      ref={ref}
      sx={{
        maxHeight: "500px",
        position: "relative",
        overflow: "auto",
      }}
    >
      {!!pdfWidth && (
        <PdfViewer
          sourceFile={contractTerms.content}
          width={pdfWidth}
          showAllPages
        />
      )}
    </Box>
  );
};

const ContractTermsPreviewModalContent = (props: ContractTermsPreviewProps) => {
  return (
    <Box>
      <ContractTermsPreview {...props} />
    </Box>
  );
};

export const openContractTermsPreviewModal = (terms: ContractTerms) => {
  ModalInstance.open({
    children: <ContractTermsPreviewModalContent contractTerms={terms} />,
    className: styles.modal,
    padding: false,
    closeButton: true,
    closeButtonProps: {
      variant: "tertiary",
      size: "small",
      className: styles.closeButton,
    },
  });
};

type BoilerplateOfferTermsSectionProps = {
  offer: EmployerOffer;
};
export const BoilerplateOfferTermsSection = ({
  offer,
}: BoilerplateOfferTermsSectionProps) => {
  const contractTerms = offer?.contract_terms;
  if (!contractTerms) {
    return null;
  }

  return (
    <BoilerplateTermsSectionWrapper sx={{ mb: { xs: 2, sm: 3 } }}>
      <Typography
        variant="label"
        size="medium"
        component="h4"
        sx={{ mb: 2, mt: "3px" }}
      >
        Contract terms
      </Typography>
      <ContractTermsPreview contractTerms={contractTerms} />
    </BoilerplateTermsSectionWrapper>
  );
};

const contractTermsAcceptedValidator = requiredWithSpecificMessage(
  "Contract terms must be accepted",
);

type BoilerplatePendingOfferTermsSectionProps = {
  offer: Pick<FreelancerOffer, "contract_terms" | "status">;
};

export const BoilerplatePendingOfferTermsSection = ({
  offer,
}: BoilerplatePendingOfferTermsSectionProps) => {
  const { contract_terms: contractTerms } = offer;
  const showAcceptedAtField = offerRequiresAcceptance(offer);
  if (!contractTerms) {
    return null;
  }

  return (
    <BoilerplateTermsSectionWrapper>
      <Typography variant="label" size="large" component="h2" sx={{ mb: 2 }}>
        Contract terms
      </Typography>
      <ContractTermsPreview contractTerms={contractTerms} />

      {showAcceptedAtField && (
        <Box sx={{ mt: 4 }}>
          <Field
            name="contract_terms_accepted_at"
            validate={[contractTermsAcceptedValidator]}
            component={AcceptTermsField}
            description={contractTerms.description}
          />
        </Box>
      )}
    </BoilerplateTermsSectionWrapper>
  );
};

type AcceptTermsFieldProps = TypedWrappedFieldProps<string | null> & {
  description: string;
};

const AcceptTermsField = ({
  input,
  meta,
  description,
}: AcceptTermsFieldProps) => {
  const { touched, error } = meta;
  const errorMessage = !!touched && error ? error : undefined;
  const handleChange = (_: unknown, checked: boolean) => {
    if (!checked) {
      input.onChange(null);
    } else {
      input.onChange(new Date().toISOString());
    }
  };
  return (
    <Stack sx={{ gap: 1 }}>
      <Typography component="p">{description}</Typography>
      <Checkbox
        label={"I Agree"}
        checked={!!input.value}
        onChange={handleChange}
        error={errorMessage}
        data-testid="contract-terms-checkbox"
      />
    </Stack>
  );
};
